/* eslint-disable @next/next/no-img-element */
/* eslint-disable no-nested-ternary */
import LexusHeaderImage from '@assets/headers/lexus-newsletter.png'
import ToyotaHeaderImage from '@assets/headers/toyota-newsletter.png'
import {
  AuthFill,
  Button,
  Email,
  FirstName,
  IsCurrentOwner,
  LastName,
  MarketingConsent,
  Models,
  Title,
} from '@components/form/fields'
import { FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useCampaign } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import { closeOverlay, getModels, getPageData } from '@utils/helpers'
import { formEvent } from '@utils/helpers/tracking'
import { NewsletterSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

const Newsletter = ({ models, content, tracking, brand, model }) => {
  const campaign = useCampaign()
  const [submitResponse, setSubmitResponse] = useState('')
  const initialValues = {
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    model: '',
    toyota_owner: false,
    reg_num: '',
    consent: {
      user: false,
    },
    response_code: '',
    campaign_response_code: '',
    campaign_code: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(NewsletterSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const regRequired = JSON.parse(methods.watch('toyota_owner'))

  const postMessage = (event) => {
    let data
    if (typeof event.data === 'object') {
      data = event.data
    } else {
      data = JSON.parse(event.data)
    }
    // eslint-disable-next-line no-prototype-builtins
    if (data.hasOwnProperty('sendNewsletterEmailAddress')) {
      if (data.sendNewsletterEmailAddress) {
        methods.setValue('email', data.sendNewsletterEmailAddress)
      }
    }
  }

  useEffect(() => {
    const isOwner = JSON.parse(methods.getValues('toyota_owner'))
    if (!isOwner) {
      methods.resetField('reg_num')
    }
  }, [regRequired])

  useEffect(() => {
    if (window.self !== window.top) {
      // eslint-disable-next-line no-restricted-globals
      parent.postMessage('requestNewsletterEmailAddress', '*')
    }
    window.addEventListener('message', postMessage, false)

    return () => {
      window.removeEventListener('message', postMessage, false)
    }
  }, [])

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }

    if (campaign?.brochure) {
      methods.setValue('campaign_response_code', campaign.brochure)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  useEffect(() => {
    if (model) {
      methods.setValue('model', model.d4t4.id)
      methods.trigger('model')
    }
  }, [model])

  const onSubmit = async (data) => {
    const formData = data
    const selectedModel = models.filter(
      (el) => el.d4t4.id === formData.model,
    )[0]

    formData.response_code = brand === 'lexus' ? 'RC60284' : 'RC81050'

    if (brand === 'toyota' && selectedModel?.responsecodes?.national_newsletter)
      formData.response_code = selectedModel?.responsecodes?.national_newsletter

    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/newsletter`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: formData,
    })
      .then(() => {
        formEvent({
          conversion: 1,
          name: tracking.form,
          step: 'form-submit',
          variant: '',
          page: tracking.page,
        })
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
          brand === 'lexus',
        )
      })
      .catch(() => {
        formEvent({
          conversion: 0,
          name: tracking.form,
          step: 'form-error',
          variant: '',
          page: tracking.page,
        })
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      {brand === 'lexus' ? (
        <Nav
          title={content.title}
          logoType={content?.logotype}
          customLogo={content?.customlogo}
          background={LexusHeaderImage}
        />
      ) : (
        <Nav
          title={content.title}
          logoType={content?.logotype}
          customLogo={content?.customlogo}
          background={ToyotaHeaderImage}
        />
      )}
      <FormWrapper>
        <FormStep
          title={content.details.title}
          subtitle={content.details.subtitle}
          active={true}
        >
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <Title />
              <FirstName />
              <LastName />
              <Email />
              {brand !== 'lexus' && (
                <>
                  <Models models={models} label="Model of interest" optional />
                  <IsCurrentOwner />
                </>
              )}
              <MarketingConsent />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'newsletter'
  const content = await import(`../../content/${brand}/forms/${form}.md`).catch(
    () => null,
  )
  const settings = await import(
    `../../content/${brand}/settings/global.md`
  ).catch(() => null)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      models: getModels(brand, form),
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: 'newsletter',
        form: 'lead-newsletter',
        variant: '',
        initialStep: 'contact-details',
      },
      description: '',
      ...(await getPageData(context, form)),
    },
  }
}

Newsletter.whyDidYouRender = true

export default Newsletter
